<template>
    <div class="stack-s" v-fs-block>
        <div class="grid-fitted">
            <span class="display-inline-block inline-m margin-bottom-s"><v-icon icon="calendar" class="inline-xs"/>Customer for {{ accountDuration }}</span>
            <span class="display-inline-block inline-m margin-bottom-s"><v-icon icon="id" class="inline-xs"/>{{ customer.id }}</span>
        </div>
        <div class="grid-fitted">
            <div class="display-inline-block inline-m margin-bottom-s" v-if="primaryAddress.hasAddress">
                <v-icon icon="map-marker" class="inline-xs"/>
                <span>{{primaryAddress.fullAddress | join}}</span>
            </div>
            <span v-if="primaryAddress.primaryPhoneNumber" class="display-inline-block inline-m margin-bottom-s"><v-icon icon="phone" class="inline-xs"/>{{primaryAddress.primaryPhoneNumber}}</span>
            <span v-if="primaryAddress.primaryEmailAddress" class="display-inline-block customer-summary-email margin-bottom-s">
                <span class="text-blue cursor-pointer inline-xs" @click="openEmailModal">
                    <v-icon icon="email" class="inline-xs"/>{{primaryAddress.primaryEmailAddress}}
                </span>
                <v-icon-button @click="copyEmail(primaryAddress.primaryEmailAddress)" tooltip="Copy email address" class="inline-xxs" type="basic" fitted>
                    <v-icon icon="clone" />
                </v-icon-button>
                <v-icon-button @click="emailDomainURL(primaryAddress.primaryEmailAddress)" tooltip="Visit domain" type="basic" fitted>
                    <v-icon icon="external-link" />
                </v-icon-button>
            </span>
        </div>
        <template v-if="customer.hasCustomFields">
            <div class="grid-fitted">
                <v-icon icon="custom-fields" class="inline-xs"/>
                <span
                    v-for="(field, index) in customFields"
                    :key="field.name"
                    :title="field.name"><span class="text-muted">{{field.name}}:</span> {{field.value}}<span v-if="customFields.length - 1 > index" class="text-muted margin-left-s margin-right-s">&bull;</span>
                </span>
            </div>
        </template>
        <v-modal title="Send Email" @close="showSendEmailModal = false" size="large" scroll v-if="showSendEmailModal">
            <div slot="content">
                <div class="grid-content">
                    <div class="grid-item grid-item-full" v-if="$v.$error">
                        <v-error-messages>
                            <li class="list-item" v-if="!$v.emailMessage.subject.required">Please provide a subject for the email.</li>
                            <li class="list-item" v-if="!$v.emailMessage.text.required">Please provide a body for the email.</li>
                        </v-error-messages>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="To"
                            disabled
                            v-model="emailMessageTo"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Subject"
                            :validate="$v.emailMessage.subject"
                            v-model="emailMessage.subject"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Email Body"
                            multiline
                            placeholder="Your message here"
                            rows="8"
                            :validate="$v.emailMessage.text"
                            v-model="emailMessage.text"/>
                    </div>
                </div>
            </div>
            <template slot="actions">
                <r-button
                    type="primary" @click="sendEmail">Send</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import {
        vIcon,
        vIconButton,
        vErrorMessages,
    } from '@/components/form';
    import {required} from 'vuelidate/lib/validators';
    import vModal from '@/components/modal/v-modal';
    import _ from '@/common/helpers';
    import EmailMessageModel from '@/common/models/email-message-model';
    import CustomFieldsValuesMixin from '@/mixins/custom-fields-values-mixin';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import Clipboard from '@/common/clipboard';
    import {mapState, mapGetters, mapActions} from 'vuex';

    export default {
        name: 'customer-summary',
        mixins: [
            CustomFieldsValuesMixin,
            LoadStateMixin,
        ],
        components: {
            vIcon,
            vModal,
            vIconButton,
            vErrorMessages,
        },
        data() {
            return {
                isLoading: false,
                showSendEmailModal: false,
                emailMessageTo: '',
                emailMessage: new EmailMessageModel(),
            };
        },
        validations: {
            emailMessage: {
                subject: {required},
                text: {required},
            },
        },
        computed: {
            ...mapState('customers', ['customer', 'customFieldsSchema']),
            ...mapGetters({
                userEmail: 'user/email',
            }),
            accountDuration() {
                return _.humanizeDuration(this.customer.createdTime);
            },
            /**
             * @returns {ContactModel}
             */
            primaryAddress() {
                return this.customer.primaryAddress;
            },
            customFields() {
                return this.getCustomFields('customer', this.getCustomFieldsTypes(this.customFieldsSchema));
            },
        },
        methods: {
            ...mapActions({
                fetchCustomFieldsSchema: 'customers/fetchCustomFieldsSchema',
            }),
            async copyEmail(data) {
                try {
                    await Clipboard.copy(data);
                    this.$toast.positive('Email copied successfully');
                } catch (error) {
                    this.$toast.negative(`Something went wrong`);
                }
            },
            emailDomainURL(email) {
                const atPosition = email.indexOf('@') + 1;
                window.open(`https://www.${email.slice(atPosition)}`, '_blank');
            },
            openEmailModal() {
                this.$v.$reset();
                this.emailMessageTo = this.primaryAddress.primaryEmailAddress;
                this.emailMessage = new EmailMessageModel({
                    to: [this.emailMessageTo],
                    from: this.userEmail,
                    subject: `Dear ${this.primaryAddress.fullName}`,
                    text: null,
                    metadata: {
                        eventType: 'direct-email-sent',
                    },
                });
                this.showSendEmailModal = true;
            },
            async sendEmail() {
                const data = this.emailMessage.getPayload();
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.showSendEmailModal = false;
                    try {
                        const message = await this.$api(({emailMessages}) => emailMessages.create({data}));
                        await this.$api(({emailMessages}) => emailMessages.send({id: message.fields.id}));
                        this.$toast.positive('Email has been sent.');
                        this.$emit('email-sent');
                    } catch (error) {
                        this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                    }
                }
            },
            async fetchData() {
                try {
                    this.fetchCustomFieldsSchema();
                } catch (error) {
                    this.$toast.fromError(error, 'Unable retrieve customer data. Please try again later or contact support.');
                }
            },
        },
    };
</script>
